import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppContext, RewardMethod } from "../../context/AppContext";
import { useCopy } from "../../context/CopyContext";
import { CreditCard, Nfc, Ticket } from "lucide-react";
import Header from "../Header";
import Heading from "../Heading";
import Button from "../Button";
import MultiLineCopy from "../MultiLineCopy";
import PoweredBy from "../PoweredBy";
import ItemsModalView from "./ItemsModalView";

const RewardMethodSelectionView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const { getCopy } = useCopy();
  const [showItemsModal, setShowItemsModal] = useState(false);

  // Calculate total rewards
  const totalRewards = state.empties.reduce(
    (totals, empty) => {
      if (empty.product.isBranded && empty.product.rewards) {
        totals.points += empty.product.rewards.loyaltyPoints * empty.quantity;
        // Parse dollar value (remove $ and convert to number)
        const dollarAmount = parseFloat(
          empty.product.rewards.dollarValue.replace("$", "")
        );
        totals.dollars += dollarAmount * empty.quantity;
      }
      return totals;
    },
    { points: 0, dollars: 0 }
  );

  // Check if there are any eligible items with rewards
  const hasEligibleItems = state.empties.some(
    (empty) => empty.product.isBranded && empty.product.rewards
  );

  const handleLoyaltyPoints = () => {
    dispatch({ type: "SET_REWARD_METHOD", payload: RewardMethod.LOYALTY_POINTS });
    console.log("User selected loyalty points");
    navigate("/summary");
  };

  const handleDigitalCash = () => {
    dispatch({ type: "SET_REWARD_METHOD", payload: RewardMethod.DIGITAL_CASH });
    console.log("User selected digital cash");
    navigate("/summary");
  };

  const handleDiscountCoupon = () => {
    // Calculate average discount percentage - no fallback for non-eligible items
    const avgDiscount = (() => {
      if (state.empties.length > 0) {
        const totalDiscount = state.empties.reduce((avg, empty) => {
          if (empty.product.isBranded && empty.product.rewards) {
            return (
              avg +
              parseInt(empty.product.rewards.discountValue) * empty.quantity
            );
          }
          return avg;
        }, 0);
        const eligibleItems = state.empties.reduce((total, empty) => {
          if (empty.product.isBranded && empty.product.rewards) {
            return total + empty.quantity;
          }
          return total;
        }, 0);
        return eligibleItems > 0
          ? Math.round(totalDiscount / eligibleItems)
          : 0;
      }
      return 0;
    })();

    dispatch({ type: "SET_REWARD_METHOD", payload: RewardMethod.DISCOUNT_COUPON });
    console.log("User selected discount coupon");
    navigate("/summary");
  };

  const handleBack = () => {
    navigate("/membership-selection");
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  return (
    <div
      className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden"
      data-node-id="164:4724"
    >
      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col">
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              emailAddress={state.userEmail}
              showEmailAddress={!!state.userEmail}
              isGuest={state.isGuest}
              isMember={!state.isGuest}
              animateIcon={true} // Animate icon since user just selected membership
              onItemsClick={handleItemsClick}
            />
          </div>
        </div>

        {/* Main Content Area - Animated with 3-row grid */}
        <motion.div
          className="flex-1 grid grid-rows-3 gap-0 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Rows 1 & 2 - Main heading spanning two rows */}
          <div className="row-span-2 flex items-center justify-center">
            <div className="text-center w-full max-w-[920px]">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.6,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <Heading
                  title={
                    state.isGuest
                      ? "Guest Checkout"
                      : state.isMember && state.userEmail && state.isSignup
                      ? "Welcome!"
                      : state.isMember && state.userEmail
                      ? "Hi, again"
                      : "Great choice!"
                  }
                  subtitle={
                    state.isGuest
                      ? "Select how you'd like to be rewarded"
                      : "Now, select how you'd like to be rewarded"
                  }
                />
              </motion.div>
            </div>
          </div>

          {/* Row 3 - Reward method selection */}
          <div className="flex items-start justify-center">
            <motion.div
              className="w-full max-w-[920px]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.2,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              {/* Choose reward method heading */}
              <div className="text-center mb-10">
                <h3 className="font-brand-primary font-medium text-[32px] leading-[42px] text-center text-brand-text-primary tracking-[-0.32px]">
                  Choose a reward method to continue
                </h3>
              </div>

              {/* Reward method cards */}
              <div className="flex flex-row gap-10 items-stretch justify-start">
                {/* Loyalty Points Card */}
                <motion.div
                  className={`flex-1 rounded-3xl p-8 flex flex-col gap-14 items-center justify-center relative transition-colors duration-200 ${
                    state.isGuest
                      ? "border border-brand-border opacity-40"
                      : "bg-brand-background border border-brand-border cursor-pointer hover:border-brand-primary"
                  }`}
                  onClick={state.isGuest ? undefined : handleLoyaltyPoints}
                  whileTap={state.isGuest ? {} : { scale: 0.98 }}
                >
                  <div className="flex flex-col gap-6 items-center justify-center w-full">
                    <div className="flex flex-col gap-2 items-center justify-start w-full">
                      <div className="w-16 h-16 flex items-center justify-center">
                        <CreditCard
                          size={64}
                          stroke="var(--brand-text-primary)"
                          strokeWidth={1}
                        />
                      </div>
                      <div className="flex flex-col gap-2 items-start justify-start text-center w-full">
                        <h4 className="font-brand-primary font-normal text-[24px] leading-[31px] text-brand-text-primary w-full">
                          Loyalty Points
                        </h4>
                        <MultiLineCopy 
                          text={getCopy('rewardMethodSelection.loyaltyPointsCard.description')}
                          className="font-brand-body font-light text-[18px] leading-[23px] text-brand-text-secondary w-full"
                        />
                      </div>
                    </div>
                    <div className="flex flex-row gap-1 items-center justify-center border border-brand-text-primary rounded-full px-3 py-1 font-brand-secondary font-medium text-[18px] leading-[31px]">
                      <div
                        className={`font-brand-secondary font-medium text-brand-text-primary ${
                          state.isGuest ? "line-through  text-brand-text-muted" : "text-[24px] leading-[31px]"
                        }`}
                      >
                        {hasEligibleItems
                          ? totalRewards.points
                          : state.empties.length === 0
                          ? 500
                          : 0}
                      </div>
                      {state.isGuest && (
                        <div className="uppercase text-brand-accent">
                          Members Only
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>

                {/* Digital Cash Card */}
                <motion.div
                  className={`flex-1 rounded-3xl p-8 flex flex-col gap-14 items-center justify-center relative transition-colors duration-200 ${
                    state.isGuest
                      ? "border border-brand-border cursor-not-allowed opacity-40"
                      : "bg-brand-background border border-brand-border cursor-pointer hover:border-brand-primary"
                  }`}
                  onClick={state.isGuest ? undefined : handleDigitalCash}                  
                  whileTap={state.isGuest ? {} : { scale: 0.98 }}
                >
                  <div className="flex flex-col gap-6 items-center justify-center w-full">
                    <div className="flex flex-col gap-2 items-center justify-start w-full">
                      <div className="w-16 h-16 flex items-center justify-center">
                        <Nfc
                          size={64}
                          stroke="var(--brand-text-primary)"
                          strokeWidth={1}
                        />
                      </div>
                      <div className="flex flex-col gap-2 items-start justify-start text-center w-full">
                        <h4 className="font-brand-primary font-normal text-[24px] leading-[31px] text-brand-text-primary w-full">
                          Digital Cash
                        </h4>
                        <MultiLineCopy 
                          text={getCopy('rewardMethodSelection.digitalCashCard.description')}
                          className="font-brand-body font-light text-[18px] leading-[23px] text-brand-text-secondary w-full"
                        />
                      </div>
                    </div>
                    <div className="flex flex-row gap-1 items-center justify-center border border-brand-text-primary rounded-full px-3 py-1 font-brand-secondary font-medium text-[18px] leading-[31px]">
                      <div className={`font-brand-secondary font-medium text-brand-text-primary ${
                          state.isGuest ? "line-through  text-brand-text-muted" : "text-[24px] leading-[31px]"
                        }`}>
                        $
                        {hasEligibleItems
                          ? totalRewards.dollars.toFixed(2)
                          : state.empties.length === 0
                          ? "0.50"
                          : "0.00"}
                      </div>
                      {state.isGuest && (
                    <div className="uppercase text-brand-accent">
                      Members Only
                    </div>
                  )}
                    </div>
                  </div>                  
                </motion.div>

                {/* Discount Coupon Card */}
                <motion.div
                  className="flex-1 bg-brand-background border border-brand-border rounded-3xl p-8 flex flex-col gap-14 items-center justify-center cursor-pointer hover:border-brand-primary transition-colors duration-200"
                  onClick={handleDiscountCoupon}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex flex-col gap-6 items-center justify-center w-full">
                    <div className="flex flex-col gap-2 items-center justify-center w-full">
                      <div className="w-16 h-16 flex items-center justify-center">
                        <Ticket
                          size={64}
                          stroke="var(--brand-text-primary)"
                          strokeWidth={1}
                        />
                      </div>
                      <div className="flex flex-col gap-2 items-center justify-center text-center w-full">
                        <h4 className="font-brand-primary font-normal text-[24px] leading-[31px] text-brand-text-primary w-full">
                          Discount Coupon
                        </h4>
                        <p className="font-brand-body font-light text-[18px] leading-[23px] text-brand-text-secondary w-full">
                          Provided as a unique coupon code.
                        </p>
                      </div>
                    </div>
                    <div className="border border-brand-text-primary rounded-full px-3 py-1">
                      <span className="font-brand-secondary font-medium text-[24px] leading-[31px] text-brand-text-primary">
                        {(() => {
                          if (state.empties.length > 0) {
                            const totalDiscount = state.empties.reduce(
                              (avg, empty) => {
                                if (
                                  empty.product.isBranded &&
                                  empty.product.rewards
                                ) {
                                  return (
                                    avg +
                                    parseInt(
                                      empty.product.rewards.discountValue
                                    ) *
                                      empty.quantity
                                  );
                                }
                                return avg;
                              },
                              0
                            );
                            const eligibleItems = state.empties.reduce(
                              (total, empty) => {
                                if (
                                  empty.product.isBranded &&
                                  empty.product.rewards
                                ) {
                                  return total + empty.quantity;
                                }
                                return total;
                              },
                              0
                            );
                            const percentage =
                              eligibleItems > 0
                                ? Math.round(totalDiscount / eligibleItems)
                                : 0;
                            return percentage > 0
                              ? `${percentage}% OFF`
                              : "0% OFF";
                          }
                          return hasEligibleItems ? "5% OFF" : "0% OFF";
                        })()}
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Bottom Section - Back button and footer */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Back Button */}
          <div className="flex justify-center w-full max-w-[282px]">
            <Button variant="tertiary" onClick={handleBack}>
              Back
            </Button>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView isOpen={showItemsModal} onClose={handleCloseItemsModal} />
    </div>
  );
};

export default RewardMethodSelectionView;
